import {RenderOptions} from "/@res@/litHtml/lib/render-options.js";
import {AttributeCommitter, BooleanAttributePart, DefaultTemplateProcessor, directive, EventPart, NodePart, Part, parts, PropertyCommitter, render, reparentNodes, templateFactory, TemplateResult} from "/@res@/litHtml/lit-html.js";
import {until} from "/@res@/litHtml/directives/until.js";
import {BaseElement} from "/@back@/commons/basis.js";
import {DOM} from "/@lib@/commons/xml/dom.js";

const range = document.createRange();
range.setStart(document.documentElement, 0);

export class ContextualTemplateResult extends TemplateResult {
	getTemplateElement(): HTMLTemplateElement {
		try {
			const template = document.createElement('template');
			template.content.appendChild(range.createContextualFragment(this.getHTML()));
			return template;
		} catch (e) {
			throw Error("Invalid lit template : " + this.getHTML());
		}
	}
}

export class AttrPrefixTemplateProcessor extends DefaultTemplateProcessor {
	handleAttributeExpressions(
		element: Element, name: string, strings: string[],
		options: RenderOptions): Part[] {
		if (name[1] === '.') {
			switch (name[0]) {
			case 'p':
				return new PropertyCommitter(element, name.slice(2), strings).parts;
			case 'e':
				return [new EventPart(element, name.slice(2), options.eventContext)];
			case 'b':
				return [new BooleanAttributePart(element, name.slice(2), strings)];
			case 'i':
				return [new InitPart(element)];
			case 'c':
				return [new ConstructorPart(element)];
			}
		}
		return new AttributeCommitter(element, name, strings).parts;
	}
}

export class InitPart implements Part {

	value: any;

	constructor(private element: Element) {}

	setValue(value: any) {
		this.value = value;
	}

	commit() {
		if (this.element instanceof BaseElement) {
			//on init au commit pour que les précédents attributs aient été déjà affectés.
			this.element.initialize(this.value);
			this.element = null;
			this.value = null;
		}
	}
}

export class ConstructorPart implements Part {

	value: (element: Element) => void;

	constructor(private element: Element) {}

	setValue(value: any) {
		this.value = value;
	}

	commit() {
		this.value(this.element);
	}
}

export const attrPrefixTemplateProcessor = new AttrPrefixTemplateProcessor();

export const xhtml = (strings: TemplateStringsArray, ...values: any[]) =>
	new ContextualTemplateResult(strings, values, 'xhtml', attrPrefixTemplateProcessor);


export class SVGTemplateResult extends TemplateResult {
	getHTML() {
		return `<svg xmlns="${DOM.SVG_NS}">${super.getHTML()}</svg>`;
	}
	getTemplateElement() {
		const template = super.getTemplateElement();
		const content = template.content;
		const svgElement = content.firstChild;
		content.removeChild(svgElement);
		reparentNodes(content, svgElement.firstChild);
		return template;
	}
}

export const svg = (strings: TemplateStringsArray, ...values: any[]) =>
	new SVGTemplateResult(strings, values, 'svg', attrPrefixTemplateProcessor);
/**
 * Variante de render() qui ajoute les noeuds aux fils existants au lieu de les supprimer.
 * Très pratique pour les shadowRoot qui ont un ou plusieurs tag <style> au début.
 * Les appels suivants à renderAppend() remplaceront les noeuds ajoutés au 1er
 * appel.
 */
export const renderAppend = (result: TemplateResult, container: Element | DocumentFragment, options?: Partial<RenderOptions>) => {
	let part = parts.get(container);
	if (part === undefined) {
		parts.set(container, part = new NodePart({templateFactory, ...options}));
		part.appendInto(container);
	}
	part.setValue(result);
	part.commit();
};

export {TemplateResult, render, directive, until};
